import { Injectable } from '@angular/core';
import { SystemDetails, ApplicationConfig, SapSystemSubTypes, SystemTypeConstants } from '@frontend-site/shared/data';
import { SessionRepository, SystemStore } from '@frontend-site/shared/core';
import { isSalesforceSystem, isSapOrOracleSystem } from '@frontend-site/shared/core';
import { combineLatest, filter, map, Observable } from 'rxjs';
import { DefaultMainHeaderMenu, MainHeaderMenu, MenuStates } from '../../models';
import { MenuBuilderService } from './menu-builder';
import { MENU_IDS } from '../menu-ids';

@Injectable()
export class InSystemMenuBuilderService implements MenuBuilderService {
    get menuState(): MenuStates {
        return MenuStates.SYSTEM;
    }

    constructor(private readonly systemStore: SystemStore, private readonly sessionStore: SessionRepository) {}

    build$(): Observable<MainHeaderMenu | null> {
        return combineLatest([this.systemStore.systemDetails$, this.sessionStore.applicationConfig$]).pipe(
            filter(([systemDetails]) => !!systemDetails),
            map(([systemDetails, appConfig]) => this.createSystemMenu(systemDetails as SystemDetails, appConfig as ApplicationConfig))
        );
    }

    private createSystemMenu(systemDetails: SystemDetails, appConfig: ApplicationConfig): MainHeaderMenu | null {
        const menu = new DefaultMainHeaderMenu(this.menuState, { title: systemDetails.systemName });

        if (this.showOrgOverview(systemDetails, appConfig)) {
            menu.addMenuItem({
                id: MENU_IDS.SYSTEM.ORGANIZATION_OVERVIEW,
                label: 'MyERPs.NavBar.organizationOverview',
                routePath: `/myERPs/${systemDetails.systemId}/organizationOverview`
            });
        }

        if (isSalesforceSystem(systemDetails.systemType)) {
            menu.addMenuItem({
                id: MENU_IDS.SYSTEM.PROJECT,
                label: 'MyERPs.NavBar.projects',
                routePath: `/myERPs/${systemDetails.systemId}/projects`
            });

            return menu;
        }

        if (appConfig.isSelectedUatOnly(systemDetails.systemId)) {
            if (appConfig?.getSessionInformation().panayaTestsAssetEnabled) {
                menu.addMenuItem({
                    id: MENU_IDS.SYSTEM.TEST_ASSETS,
                    label: 'MyERPs.NavBar.testAssets',
                    routePath: `/myERPs/${systemDetails.systemId}/testAssets/panayaTests`
                });
            }
            return menu;
        }

        if (this.showSystemDefaultMenu(systemDetails, appConfig)) {
            if (isSapOrOracleSystem(systemDetails.systemType)) {
                menu.addMenuItem({
                    id: MENU_IDS.SYSTEM.SNAPSHOT,
                    label: 'MyERPs.NavBar.ERPSnapshot',
                    routePath: `/myERPs/${systemDetails.systemId}/ERPSnapshot`
                });
            }
            if (this.showSystemFitnessMenuItem(systemDetails)) {
                menu.addMenuItem({
                    id: MENU_IDS.SYSTEM.FITNESS,
                    label: 'MyERPs.NavBar.ERPFitnessState',
                    routePath: `/myERPs/${systemDetails.systemId}/fitness`
                });
            }
            if (this.showSystemSatelliteMenuItem(systemDetails, appConfig)) {
                menu.addMenuItem({
                    id: MENU_IDS.SYSTEM.SATELLITE,
                    label: 'MyERPs.NavBar.satelliteSystems',
                    routePath: `/myERPs/${systemDetails.systemId}/satellite`
                });
            }

            if (appConfig?.getSessionInformation().panayaTestsAssetEnabled) {
                menu.addMenuItem({
                    id: MENU_IDS.SYSTEM.TEST_ASSETS,
                    label: 'MyERPs.NavBar.testAssets',
                    routePath: `/myERPs/${systemDetails.systemId}/testAssets/panayaTests`
                });
            } else {
                menu.addMenuItem({
                    id: MENU_IDS.SYSTEM.TEST_ASSETS,
                    label: 'MyERPs.NavBar.testAssets',
                    routePath: `/myERPs/${systemDetails.systemId}/testAssets/testCoverage`
                });
            }

            if (isSapOrOracleSystem(systemDetails.systemType)) {
                menu.addMenuItem({
                    id: MENU_IDS.SYSTEM.TEST_ASSETS,
                    label: 'MyERPs.NavBar.changeStream',
                    routePath: `/myERPs/${systemDetails.systemId}/changeStream`
                });
            }

            return menu;
        }

        return null;
    }

    private showOrgOverview(systemDetails: SystemDetails, appConfig: ApplicationConfig): boolean {
        return (
            systemDetails.modular &&
            systemDetails.modules.includes('ORG_OVERVIEW') &&
            (systemDetails.orgOverviewVisible || appConfig.isExecuteUser)
        );
    }

    private showSystemDefaultMenu(systemDetails: SystemDetails, appConfig: ApplicationConfig): boolean {
        return (
            (!appConfig.uatOnly && (appConfig.iaEnabled || appConfig.rdxEnabled)) ||
            (systemDetails.systemEtlExist && !systemDetails.systemDummyEtl && !appConfig.uatOnly && !appConfig.rdxEnabled)
        );
    }

    private showSystemFitnessMenuItem(system: SystemDetails): boolean {
        return (
            (system.systemType === SystemTypeConstants.SAP || system.systemType === SystemTypeConstants.ORACLE) &&
            system.accountSubType !== SapSystemSubTypes.SCM
        );
    }
    private showSystemSatelliteMenuItem(system: SystemDetails, appConfig: ApplicationConfig): boolean {
        return system.systemType === SystemTypeConstants.SAP && (system.satelliteEnabled || appConfig.panayaUser);
    }
}
